'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { api, useTranslation, useUi } from '@weareconceptstudio/core';
import { useCart } from '@weareconceptstudio/cart';
import { useAccountContext, useAddressContext } from '@weareconceptstudio/account';
import DeliveryTemplate from '@/modules/Checkout/DeliveryTemplate';
import { formatAddressTitle, formatScheduledDateLabel, normalizeBranchText } from './utils';

import { getHasFulfillmentSelection, readPendingAddress } from '@/helpers/fulfillmentGuard';
import { useShopFulfillmentEntryPrompt } from '@/hooks/useShopFulfillmentEntryPrompt';
import { LOCALES } from '@/constants';

export const useFulfillmentChips = ({
	compact,
	iconOnly,
	allowOnHomePage = false,
	promptOnShopEnter = false,
}) => {
	const pathname = usePathname();
	const { openPopup } = useUi();
	const { translate, selectedLang } = useTranslation();
	const { checkoutData, fillCheckoutData } = useCart();
	const { fulfillment, isFulfillmentEnabled, delivery, useUser } = useAccountContext();
	const { isLoggedIn } = useUser();
	const { selectedAddresses, hasAddressType } = useAddressContext();

	// Reactive pending address — updates when guest saves an address without logging in
	const [pendingAddress, setPendingAddress] = useState(readPendingAddress);
	useEffect(() => {
		const handler = (e) => setPendingAddress(e.detail || null);
		window.addEventListener('pendingAddressSaved', handler);
		return () => window.removeEventListener('pendingAddressSaved', handler);
	}, []);

	const pathSegments = pathname.split('/').filter(Boolean);
	const isHomePage =
		pathSegments.length === 0 || (pathSegments.length === 1 && LOCALES.includes(pathSegments[0]));

	const isEnabled = isFulfillmentEnabled || delivery?.enabled !== false;
	const branches = fulfillment?.branches || [];
	const fulfillmentType = checkoutData?.fulfillmentType || 'delivery';
	const isTakeaway = fulfillmentType === 'takeaway';

	const selectedBranch = useMemo(
		() => branches.find((b) => Number(b.id) === Number(checkoutData?.fulfillmentBranchId)) || null,
		[branches, checkoutData?.fulfillmentBranchId]
	);

	const selectedAddress = useMemo(() => {
		if (hasAddressType) return selectedAddresses?.shipping || null;
		return selectedAddresses || null;
	}, [hasAddressType, selectedAddresses]);

	const hasFulfillmentSelection = useMemo(
		() =>
			getHasFulfillmentSelection({
				checkoutData,
				selectedAddresses,
				hasAddressType,
				isLoggedIn,
				branches,
				pendingAddress,
			}),
		[checkoutData, selectedAddresses, hasAddressType, isLoggedIn, branches, pendingAddress]
	);

	const displayIsTakeaway = hasFulfillmentSelection ? isTakeaway : false;

	const locationLabel = useMemo(() => {
		if (displayIsTakeaway && selectedBranch) return normalizeBranchText(selectedBranch.name);
		if (!displayIsTakeaway && selectedAddress) return formatAddressTitle(selectedAddress);
		if (!isLoggedIn && pendingAddress) {
			return pendingAddress.address || pendingAddress.address_line_1 || pendingAddress.name || null;
		}
		return null;
	}, [displayIsTakeaway, selectedBranch, selectedAddress, isLoggedIn, pendingAddress]);

	const homeStyleLabel = useMemo(
		() => translate('deliveryTemplate.addressTakeawayPlaceholder'),
		[translate]
	);

	const displayLabel = hasFulfillmentSelection ? locationLabel : homeStyleLabel;

	const timeLabel = useMemo(() => {
		if (checkoutData?.fulfillmentType === 'asap') return translate('fulfillmentChips.asap');
		if (!checkoutData?.scheduledDate || !checkoutData?.scheduledTime) return null;

		const dateLabel = formatScheduledDateLabel(checkoutData.scheduledDate, {
			translate,
			locale: selectedLang,
		});

		if (!dateLabel) return null;

		return translate('fulfillmentChips.scheduledAt', {
			date: dateLabel,
			time: checkoutData.scheduledTime,
		});
	}, [checkoutData?.fulfillmentType, checkoutData?.scheduledDate, checkoutData?.scheduledTime, selectedLang, translate]);

	useEffect(() => {
		if (!hasFulfillmentSelection || !locationLabel) return;
		if (checkoutData?.fulfillmentType === 'asap') return;
		if (checkoutData?.scheduledDate && checkoutData?.scheduledTime) return;

		if (!isTakeaway && fulfillment?.asap_available) {
			fillCheckoutData({
				fulfillmentType: 'asap',
				scheduledDate: null,
				scheduledTime: null,
			});
			return;
		}

		const rawDates = isTakeaway
			? fulfillment?.takeaway_dates || fulfillment?.dates
			: fulfillment?.scheduled_delivery_dates || fulfillment?.dates;

		const dates = (Array.isArray(rawDates) ? rawDates : [])
			.filter((d) => d?.available !== false)
			.map((d) => (typeof d === 'string' ? d : d.date))
			.filter(Boolean);

		if (!dates.length) return;

		const nearestDate = dates[0];
		const branchId = isTakeaway ? checkoutData?.fulfillmentBranchId : null;
		const primaryType = isTakeaway ? 'takeaway' : 'scheduled_delivery';
		const fallbackType = isTakeaway ? 'scheduled_delivery' : null;

		const fetchSlots = (type) =>
			api
				.get({
					url: 'fulfillment/slots',
					params: { type, date: nearestDate, ...(branchId ? { branchId } : {}) },
				})
				.then((res) => res?.slots || [])
				.catch(() => []);

		fetchSlots(primaryType)
			.then((slots) => (slots.length || !fallbackType ? slots : fetchSlots(fallbackType)))
			.then((slots) => {
				const firstSlot = slots[0] || null;
				if (firstSlot) fillCheckoutData({ scheduledDate: nearestDate, scheduledTime: firstSlot });
			});
	}, [
		hasFulfillmentSelection,
		locationLabel,
		isTakeaway,
		fulfillment,
		checkoutData?.fulfillmentType,
		checkoutData?.fulfillmentBranchId,
		checkoutData?.scheduledDate,
		checkoutData?.scheduledTime,
		fulfillment?.asap_available,
		fillCheckoutData,
	]);

	const openDeliveryPopup = useCallback(() => {
		openPopup(
			<DeliveryTemplate
				initialTab={displayIsTakeaway ? 'takeaway' : 'delivery'}
				redirectAfterConfirm={isHomePage}
			/>,
			{
				className: 'delivery-template-popup',
			}
		);
	}, [displayIsTakeaway, isHomePage, openPopup]);

	const wrapperClassName = [compact ? 'compact' : '', iconOnly ? 'icon-only' : ''].filter(Boolean).join(' ');
	const isVisible = isEnabled && (!isHomePage || allowOnHomePage);

	const addressesResolvedRef = useRef(false);
	const [dataReady, setDataReady] = useState(false);

	useEffect(() => {
		if (addressesResolvedRef.current) return;
		if (!isLoggedIn) {
			addressesResolvedRef.current = true;
			setDataReady(true);
			return;
		}
		if (checkoutData?.fulfillmentType === 'takeaway') {
			addressesResolvedRef.current = true;
			setDataReady(true);
			return;
		}
		if (selectedAddresses !== null) {
			addressesResolvedRef.current = true;
			setDataReady(true);
		}
	}, [isLoggedIn, checkoutData?.fulfillmentType, selectedAddresses]);

	useShopFulfillmentEntryPrompt({
		enabled: promptOnShopEnter && isVisible && dataReady,
		hasFulfillmentSelection,
		initialTab: isTakeaway ? 'takeaway' : 'delivery',
	});

	return {
		isVisible,
		wrapperClassName,
		isTakeaway: displayIsTakeaway,
		hasFulfillmentSelection,
		iconOnly,
		locationLabel: displayLabel,
		timeLabel: hasFulfillmentSelection ? timeLabel : null,
		openDeliveryPopup,
	};
};
